import * as React from 'react';

import { faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DayPicker, type DropdownProps } from 'react-day-picker';
import { bg, enUS, type Locale } from 'react-day-picker/locale';

import { buttonVariants } from 'src/registry/ui/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { storage } from 'src/registry/utils/storage';
import { cn } from 'src/utils/shadcn';

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {};

// Keyed by language only, since apps store either a bare language ('bg') or a full locale ('bg-BG').
const calendarLocal: Record<string, Locale> = {
    en: enUS,
    bg,
};

// Without explicit bounds react-day-picker limits the year dropdown to [today - 100y, current year], which would
// make future dates unreachable. Only applied when a dropdown caption layout is used.
const DROPDOWN_START_MONTH = new Date(1900, 0);
const DROPDOWN_END_MONTH = new Date(2099, 11);

// Open the calendar on the month of the current selection rather than on today's month.
const getSelectedMonth = (props: CalendarProps): Date | undefined => {
    if (!('selected' in props) || !props.selected) return undefined;
    if (props.selected instanceof Date) return props.selected;
    if (Array.isArray(props.selected)) return props.selected[0];
    return props.selected.from;
};

function CalendarDropdown({ options, value, onChange, disabled, 'aria-label': ariaLabel }: DropdownProps) {
    return (
        <Select
            value={String(value)}
            disabled={disabled}
            onValueChange={(newValue) =>
                onChange?.({ target: { value: newValue } } as React.ChangeEvent<HTMLSelectElement>)
            }
        >
            <SelectTrigger
                aria-label={ariaLabel}
                className="h-8 w-auto gap-1 border-transparent px-1.5 text-sm font-medium capitalize shadow-none [&>svg]:mr-0 [&>svg]:w-3"
            >
                <SelectValue />
            </SelectTrigger>
            <SelectContent style={{ maxHeight: 'min(18rem, var(--radix-select-content-available-height))' }}>
                {options?.map((option) => (
                    <SelectItem
                        key={option.value}
                        value={String(option.value)}
                        disabled={option.disabled}
                        className="capitalize"
                    >
                        {option.label}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

/**
 * Pass `captionLayout="dropdown"` (or `"dropdown-months"` / `"dropdown-years"`) to replace the month caption with
 * month and/or year selects. The year range defaults to 1900–2099 and can be narrowed with `startMonth`/`endMonth`.
 */
function Calendar({
    className,
    classNames,
    showOutsideDays = true,
    captionLayout = 'label',
    startMonth,
    endMonth,
    month: monthProp,
    defaultMonth,
    onMonthChange,
    ...props
}: CalendarProps) {
    const language = storage.getLanguage()?.split('-')[0];
    const [month, setMonth] = React.useState(() => defaultMonth ?? getSelectedMonth(props as CalendarProps));
    const hasDropdowns = captionLayout !== 'label';

    return (
        <DayPicker
            month={monthProp ?? month}
            onMonthChange={(newMonth) => {
                setMonth(newMonth);
                onMonthChange?.(newMonth);
            }}
            captionLayout={captionLayout}
            startMonth={startMonth ?? (hasDropdowns ? DROPDOWN_START_MONTH : undefined)}
            endMonth={endMonth ?? (hasDropdowns ? DROPDOWN_END_MONTH : undefined)}
            locale={calendarLocal[language]}
            showOutsideDays={showOutsideDays}
            className={cn('p-3', className)}
            classNames={{
                months: 'relative flex flex-col sm:flex-row gap-4 sm:space-y-0',
                month: 'space-y-4',
                // With dropdowns, keep the caption clear of the absolutely positioned size-8 nav buttons.
                month_caption: cn('flex justify-center relative items-center', hasDropdowns ? 'h-8 px-9' : 'pt-1.5'),
                caption_label: 'text-sm font-medium',
                dropdowns: 'flex items-center gap-1',
                nav: 'space-x-1 z-20 flex items-center absolute top-0 w-full',
                button_previous: cn(buttonVariants({ variant: 'outline-solid' }), 'size-8 p-0 top-0 absolute left-1'),
                button_next: cn(buttonVariants({ variant: 'outline-solid' }), 'size-8 p-0 top-0 absolute right-1'),
                month_grid: 'w-full border-collapse space-y-1',
                weekdays: 'flex',
                weekday: 'text-gray-500 rounded-md w-9 font-normal text-[0.8rem] dark:text-gray-400',
                week: 'flex w-full mt-2 rounded-md overflow-hidden',
                day: 'h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-gray-100/50 [&:has([aria-selected])]:bg-gray-100 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20 dark:[&:has([aria-selected].day-outside)]:bg-gray-800/50 dark:[&:has([aria-selected])]:bg-gray-800',
                day_button: cn(
                    buttonVariants({ variant: 'ghost' }),
                    'h-9 w-9 p-0 font-normal aria-selected:opacity-100',
                ),
                range_end: 'bg-primary-700 text-gray-50 rounded-md hover:bg-primary-700 hover:text-gray-900',
                range_start: 'bg-primary-600 text-gray-50 rounded-md hover:bg-primary-600 hover:text-gray-900',
                today:
                    "bg-gray-100 rounded-md text-gray-900 dark:bg-gray-800 dark:text-gray-50 [&[aria-selected='true']]:bg-primary-600 [&[aria-selected='true']]:text-white   [&[aria-selected='true']:hover]:bg-primary-700\n" +
                    "  [&[aria-selected='true']:hover]:text-gray-900",
                outside:
                    'day-outside text-gray-500 opacity-50 aria-selected:bg-gray-100/50 aria-selected:text-gray-500 aria-selected:opacity-30 dark:text-gray-400 dark:aria-selected:bg-gray-800/50 dark:aria-selected:text-gray-400',
                disabled: 'text-gray-500 opacity-50 dark:text-gray-400',
                range_middle:
                    'aria-selected:bg-gray-100 aria-selected:text-gray-900 dark:aria-selected:bg-gray-800 dark:aria-selected:text-gray-50',
                hidden: 'invisible',
                selected: 'bg-primary-600 text-gray-50 rounded-md hover:bg-primary-600 hover:text-gray-900',
                ...classNames,
            }}
            components={{
                Chevron: ({ orientation, size: _, ...props }) => {
                    if (orientation === 'left') {
                        return <FontAwesomeIcon icon={faChevronLeft} {...props} className="size-3" />;
                    }
                    return <FontAwesomeIcon icon={faChevronRight} {...props} className="size-3" />;
                },
                Dropdown: CalendarDropdown,
            }}
            {...props}
        />
    );
}
Calendar.displayName = 'Calendar';

export { Calendar };
